import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, ShieldCheck, ShieldOff } from "lucide-react";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/users")
      .then((res) => setUsers(res.data.items))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const setRole = async (id, role) => {
    try {
      await api.patch(`/admin/users/${id}/role`, { role });
      toast.success(`Role set to ${role}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this user and all their results?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-8 lg:p-10">
      <div className="mb-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-2">// USERS</div>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tight" style={{ fontWeight: 800 }}>
          User management
        </h1>
        <p className="text-sm text-[#94A3B8] mt-2">{users.length} registered users</p>
      </div>

      {loading ? (
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#64748B]">// Loading…</div>
      ) : (
        <div className="bg-[#0F1525] border border-[#1E2A3F] rounded-md overflow-hidden">
          <table className="w-full" data-testid="users-table">
            <thead>
              <tr className="border-b border-[#1E2A3F] text-left">
                <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">Email</th>
                <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">Name</th>
                <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">Role</th>
                <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">Tests</th>
                <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">Premium</th>
                <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">Joined</th>
                <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-[#1E2A3F]/60 hover:bg-[#0A0E1A]/60">
                  <td className="px-5 py-3 text-sm">{u.email}</td>
                  <td className="px-5 py-3 text-sm text-[#94A3B8]">{u.name || "—"}</td>
                  <td className="px-5 py-3">
                    <span
                      className={`inline-block px-2 py-0.5 rounded font-mono text-[10px] uppercase tracking-[0.2em] ${
                        u.role === "admin"
                          ? "bg-[#3B82F6]/15 text-[#3B82F6] border border-[#3B82F6]/40"
                          : "bg-[#1E2A3F] text-[#94A3B8]"
                      }`}
                    >
                      {u.role}
                    </span>
                  </td>
                  <td className="px-5 py-3 font-mono text-sm">{u.results_count}</td>
                  <td className="px-5 py-3 font-mono text-sm text-[#00FF94]">{u.premium_count}</td>
                  <td className="px-5 py-3 font-mono text-xs text-[#64748B]">
                    {new Date(u.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      {u.role === "admin" ? (
                        <button
                          onClick={() => setRole(u.id, "user")}
                          data-testid={`demote-${u.id}`}
                          title="Demote to user"
                          className="p-2 hover:bg-[#1E2A3F] rounded text-[#94A3B8] hover:text-white"
                        >
                          <ShieldOff className="h-3.5 w-3.5" strokeWidth={1.75} />
                        </button>
                      ) : (
                        <button
                          onClick={() => setRole(u.id, "admin")}
                          data-testid={`promote-${u.id}`}
                          title="Promote to admin"
                          className="p-2 hover:bg-[#1E2A3F] rounded text-[#94A3B8] hover:text-[#3B82F6]"
                        >
                          <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.75} />
                        </button>
                      )}
                      <button
                        onClick={() => remove(u.id)}
                        data-testid={`delete-${u.id}`}
                        title="Delete user"
                        className="p-2 hover:bg-[#1E2A3F] rounded text-[#94A3B8] hover:text-[#EF4444]"
                      >
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
